/**
 * The export's last step (plan fluffy-strolling-sonnet Rev. 4 W-A.9, Gmail
 * clipping): the template literals in parse-email.ts indent every line for the
 * reader of the SOURCE, and the indentation travelled into every sent mail —
 * 27 % of the bytes of the G2 fixtures. Gmail clips a mail at ~102 KB and
 * loses its <style> (the phone layout) with it. Leading indentation goes; the
 * newline stays, so no two tokens merge and inline whitespace collapses
 * exactly as before — rendering is identical in every client, inside <style>
 * and conditional comments included (every marker sits on a line of its own).
 *
 * Authored content is never rewritten: a <pre>, a pasted `white-space:
 * pre-wrap`, a user's HTML block keep their bytes. The renderer brackets the
 * authored html in two control characters that HTML never carries
 * (`markAuthored`); the compactor skips those segments and drops the markers
 * (zero bytes added). A stray marker — one a hostile attribute value smuggled
 * in, since `escapeAttr` passes C0 characters — is stripped at the end, so
 * neither marker ever reaches the stored mail (other C0 characters pass as they
 * always did; nothing here encodes them).
 */
export const AUTHORED_OPEN = "\u0001";
export const AUTHORED_CLOSE = "\u0002";
// Built from the constants (not a literal class): the control characters are the point here.
const MARKERS = new RegExp(`[${AUTHORED_OPEN}${AUTHORED_CLOSE}]`, "g");
const INDENT = /\n[ \t]+/g;

/** Brackets authored html; markers inside it are removed so segments cannot nest. Empty stays empty. */
export const markAuthored = (html: string): string =>
  html === "" ? "" : `${AUTHORED_OPEN}${html.replace(MARKERS, "")}${AUTHORED_CLOSE}`;

/**
 * Strips the indentation after every newline outside the authored segments;
 * drops every marker. One linear pass with indexOf: a lazy regex would rescan
 * to the end from every stray OPEN that has no CLOSE after it (audit lens 4).
 */
export function compactExportWhitespace(html: string): string {
  let out = "";
  let last = 0;
  for (;;) {
    const open = html.indexOf(AUTHORED_OPEN, last);
    if (open === -1) break;
    const close = html.indexOf(AUTHORED_CLOSE, open + 1);
    if (close === -1) break; // no segment left — the stray marker dies in the final strip
    out += html.slice(last, open).replace(INDENT, "\n") + html.slice(open + 1, close);
    last = close + 1;
  }
  out += html.slice(last).replace(INDENT, "\n");
  return out.replace(MARKERS, "");
}
